import { STATUS_ORDER, slugify, type Task, type TaskStatus } from '@ai-studio/shared';
import { useEffect, useMemo, useState, type DragEvent } from 'react';

import { askAssistant } from '../lib/ask-ai.js';
import { promptText } from '../lib/prompt-dialog.js';
import { useBoard } from '../state/board.js';
import { useWorkspace } from '../state/workspace.js';
import { Icon } from './ui/icons.js';

/**
 * The game plan: what you and Prisma are making, step by step. Ideas wait in
 * "Ideas", the next steps in "Up next"; Prisma moves a task to "Prisma's on
 * it" while she works and to "Check it" when it is ready for you to try. You
 * approve it (Done) or send it back with what to change.
 */

const COLUMNS: Record<TaskStatus, { label: string; hint: string }> = {
  backlog: { label: 'Ideas', hint: 'Things you might want later' },
  ready: { label: 'Up next', hint: 'The next steps, most important first' },
  in_progress: { label: "Prisma's on it", hint: 'Being made right now' },
  review: { label: 'Check it', hint: 'Try these in your game' },
  done: { label: 'Done', hint: 'Finished and approved' },
};

/** Three plain levels instead of a 1–100 number. */
const LEVELS = [
  { id: 'must', label: 'Must have', value: 90 },
  { id: 'should', label: 'Should have', value: 60 },
  { id: 'nice', label: 'Nice to have', value: 30 },
] as const;
const levelOf = (priority: number) => (priority >= 75 ? LEVELS[0] : priority >= 45 ? LEVELS[1] : LEVELS[2]);

/** Tasks no longer have jobs to pick: Prisma does them all (the field stays for old boards). */
const DEFAULT_JOB = 'heavy_coder' as const;

const DONE_SHOWN = 12;

function NewTaskForm({ onClose }: { onClose: () => void }) {
  const projectId = useWorkspace((s) => s.projectId);
  const create = useBoard((s) => s.create);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [level, setLevel] = useState<(typeof LEVELS)[number]['id']>('should');
  const [where, setWhere] = useState<'ready' | 'backlog'>('ready');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!projectId || !title.trim()) return;
    setBusy(true);
    const created = await create(projectId, {
      title: title.trim(),
      slug: slugify(title) || undefined,
      description: description.trim() || undefined,
      job: DEFAULT_JOB,
      priority: LEVELS.find((l) => l.id === level)!.value,
      status: where,
    });
    setBusy(false);
    if (created) onClose();
  }

  return (
    <form className="plan-form" onSubmit={submit}>
      <input className="plan-form-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What should the game have? e.g. A double jump" autoFocus required maxLength={200} />
      <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Any details (optional): how it should feel, look or work" />
      <div className="plan-form-row">
        <div className="plan-seg" role="radiogroup" aria-label="How important">
          {LEVELS.map((l) => (
            <button key={l.id} type="button" role="radio" aria-checked={level === l.id} className={`plan-level is-${l.id}${level === l.id ? ' is-on' : ''}`} onClick={() => setLevel(l.id)}>
              {l.label}
            </button>
          ))}
        </div>
        <div className="plan-seg" role="radiogroup" aria-label="Where">
          <button type="button" role="radio" aria-checked={where === 'ready'} className={where === 'ready' ? 'is-on' : ''} onClick={() => setWhere('ready')}>
            Up next
          </button>
          <button type="button" role="radio" aria-checked={where === 'backlog'} className={where === 'backlog' ? 'is-on' : ''} onClick={() => setWhere('backlog')}>
            Ideas
          </button>
        </div>
        <span className="plan-spacer" />
        <button type="button" className="ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={busy || !title.trim()}>
          {busy ? 'Adding…' : 'Add'}
        </button>
      </div>
    </form>
  );
}

function TaskCard({ task, byId }: { task: Task; byId: ReadonlyMap<number, Task> }) {
  const projectId = useWorkspace((s) => s.projectId);
  const patch = useBoard((s) => s.patch);
  const remove = useBoard((s) => s.remove);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const level = levelOf(task.priority);
  const waitingOn = task.dependsOn.map((id) => byId.get(id)).filter((t): t is Task => Boolean(t) && t!.status !== 'done');

  const set = (p: Parameters<typeof patch>[2]) => (projectId ? patch(projectId, task.id, p) : Promise.resolve(false));
  const nextLevel = () => set({ priority: LEVELS[(LEVELS.indexOf(level) + 2) % 3]!.value });

  async function doIt() {
    await askAssistant(`Please work on the task "${task.slug}" from the board: ${task.title}.${task.description ? `\n\n${task.description}` : ''}`);
  }

  async function approve() {
    await set({ status: 'done' });
  }

  async function sendBack() {
    const note = await promptText('What should Prisma change? (e.g. the jump feels too floaty)');
    if (note === null) return;
    await set({ status: 'ready' });
    await askAssistant(`I tried "${task.slug}" (${task.title}) and it needs changes: ${note.trim() || 'please take another look'}. Please fix it, then put it back in Check it.`);
  }

  async function saveEdit() {
    setEditing(false);
    if (title.trim() && (title.trim() !== task.title || description !== task.description)) await set({ title: title.trim(), description });
  }

  async function del() {
    if (projectId && window.confirm(`Remove "${task.title}" from the plan?`)) await remove(projectId, task.id);
  }

  return (
    <article
      className={`plan-card is-${task.status}${task.blocked || waitingOn.length ? ' is-waiting' : ''}`}
      draggable={!editing}
      onDragStart={(e) => e.dataTransfer.setData('text/task-id', String(task.id))}
    >
      <header className="plan-card-head">
        <button type="button" className={`plan-level is-${level.id} is-on tiny`} onClick={() => void nextLevel()} title="Change how important this is">
          {level.label}
        </button>
        <span className="plan-spacer" />
        <button type="button" className="plan-icon" onClick={() => setEditing((v) => !v)} title="Edit">
          <Icon name="edit" size={12} />
        </button>
        <button type="button" className="plan-icon" onClick={() => void del()} title="Remove">
          <Icon name="x" size={12} />
        </button>
      </header>

      {editing ? (
        <div className="plan-edit">
          <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus onKeyDown={(e) => e.key === 'Enter' && void saveEdit()} />
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Details" />
          <div className="plan-form-row">
            <span className="plan-spacer" />
            <button type="button" className="ghost tiny" onClick={() => { setEditing(false); setTitle(task.title); setDescription(task.description); }}>
              Cancel
            </button>
            <button type="button" className="primary tiny" onClick={() => void saveEdit()}>
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="plan-title" onClick={() => setOpen((v) => !v)}>
            {task.title}
          </p>
          {task.description && <p className={`plan-desc${open ? ' is-open' : ''}`} onClick={() => setOpen((v) => !v)}>{task.description}</p>}
        </>
      )}

      {(task.blocked || waitingOn.length > 0) && (
        <p className="plan-wait">
          <Icon name="info" size={11} />
          {waitingOn.length ? `After: ${waitingOn.map((t) => t.title).join(', ')}` : (task.blockedReason ?? 'Waiting')}
        </p>
      )}

      <footer className="plan-card-foot">
        {(task.status === 'backlog' || task.status === 'ready') && (
          <>
            <button type="button" className="plan-go" onClick={() => void doIt()} title="Ask Prisma to make this now">
              <Icon name="sparkles" size={12} /> Prisma, do this
            </button>
            {task.status === 'backlog' ? (
              <button type="button" className="ghost tiny" onClick={() => void set({ status: 'ready' })}>
                Up next
              </button>
            ) : (
              <button type="button" className="ghost tiny" onClick={() => void set({ status: 'backlog' })}>
                Later
              </button>
            )}
          </>
        )}
        {task.status === 'in_progress' && (
          <span className="plan-working">
            <span className="plan-pulse" aria-hidden="true" /> Prisma is making this
          </span>
        )}
        {task.status === 'review' && (
          <>
            <button type="button" className="plan-ok" onClick={() => void approve()}>
              <Icon name="check" size={12} /> Looks good
            </button>
            <button type="button" className="ghost tiny" onClick={() => void sendBack()}>
              Needs changes
            </button>
          </>
        )}
        {task.status === 'done' && (
          <>
            <span className="muted small">{task.completedAt ? new Date(task.completedAt).toLocaleDateString() : 'Done'}</span>
            <span className="plan-spacer" />
            <button type="button" className="ghost tiny" onClick={() => void set({ status: 'ready' })}>
              Reopen
            </button>
          </>
        )}
      </footer>
    </article>
  );
}

export function Board() {
  const projectId = useWorkspace((s) => s.projectId);
  const board = useBoard((s) => s.board);
  const tasks = useBoard((s) => s.tasks);
  const load = useBoard((s) => s.load);
  const patch = useBoard((s) => s.patch);
  const error = useBoard((s) => s.error);
  const [composing, setComposing] = useState(false);
  const [dragOver, setDragOver] = useState<TaskStatus | null>(null);
  const [allDone, setAllDone] = useState(false);

  useEffect(() => {
    if (projectId) void load(projectId);
  }, [projectId, load]);

  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  if (!projectId) return <p className="muted explorer-hint">No project open.</p>;

  const counted = tasks.filter((t) => t.status !== 'backlog');
  const done = counted.filter((t) => t.status === 'done').length;
  const pct = counted.length ? Math.round((done / counted.length) * 100) : 0;
  const toCheck = board?.review.length ?? 0;

  async function onDrop(event: DragEvent, status: TaskStatus) {
    event.preventDefault();
    setDragOver(null);
    const taskId = Number(event.dataTransfer.getData('text/task-id'));
    if (!projectId || !Number.isFinite(taskId)) return;
    await patch(projectId, taskId, { status });
  }

  const plan = () =>
    askAssistant(
      tasks.length
        ? 'Look at my game and the task board, then update the plan: add the next small, clear steps to "Up next" (most important first), move ideas that should come next out of Ideas, and tidy anything that is out of date.'
        : "Look at my game and make a plan on the task board: break it into small, clear steps in the order to build them, and put them in Up next (the first few) and Ideas (later ones).",
    );

  return (
    <div className="plan">
      <div className="plan-head">
        <div className="plan-progress">
          <strong>Game plan</strong>
          <span className="muted small">{counted.length ? `${done} of ${counted.length} done${toCheck ? ` · ${toCheck} to check` : ''}` : 'No steps yet'}</span>
          <div className="plan-bar" aria-hidden="true">
            <div style={{ width: `${pct}%` }} />
          </div>
        </div>
        <span className="plan-spacer" />
        <button type="button" className="plan-go" onClick={() => void plan()} title="Prisma looks at your game and plans the next steps">
          <Icon name="sparkles" size={13} /> {tasks.length ? 'Update the plan with Prisma' : 'Plan my game with Prisma'}
        </button>
        <button type="button" className="primary" onClick={() => setComposing((v) => !v)}>
          {composing ? 'Cancel' : '+ Add a step'}
        </button>
      </div>

      {error && <p className="login-error">{error}</p>}
      {composing && <NewTaskForm onClose={() => setComposing(false)} />}

      <div className="plan-columns">
        {STATUS_ORDER.map((status) => {
          const all = board?.[columnKey(status)] ?? [];
          const column = status === 'done' && !allDone ? all.slice(0, DONE_SHOWN) : all;
          return (
            <section
              key={status}
              className={`plan-column is-${status}${dragOver === status ? ' is-drop' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(status);
              }}
              onDragLeave={() => setDragOver((s) => (s === status ? null : s))}
              onDrop={(e) => void onDrop(e, status)}
            >
              <header className="plan-column-head" title={COLUMNS[status].hint}>
                <span>{COLUMNS[status].label}</span>
                <span className="plan-count">{all.length}</span>
              </header>
              <div className="plan-column-body">
                {column.map((task) => (
                  <TaskCard key={task.id} task={task} byId={byId} />
                ))}
                {all.length === 0 && <p className="plan-empty">{COLUMNS[status].hint}</p>}
                {status === 'done' && all.length > DONE_SHOWN && (
                  <button type="button" className="ghost tiny" onClick={() => setAllDone((v) => !v)}>
                    {allDone ? 'Show fewer' : `Show all ${all.length}`}
                  </button>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/** BoardSnapshot's array keys differ from TaskStatus for in_progress. */
type ColumnKey = 'backlog' | 'ready' | 'inProgress' | 'review' | 'done';

function columnKey(status: TaskStatus): ColumnKey {
  return status === 'in_progress' ? 'inProgress' : status;
}
